import type { SimpleTableConfigBase } from "@/components/shared/UniversalCRMTable/types/shared";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types/base-types";
import {
  groupByBoolean,
  groupByDate,
  groupByEnum,
  groupByMultiUser,
} from "./grouping-strategies/enum-date-strategies";
import {
  groupByNumberRange,
  groupByTags,
  groupByText,
} from "./grouping-strategies/value-strategies";
import type {
  GroupableFieldConfig,
  GroupedDataItem,
  GroupedItem,
  GroupHeader,
  GroupingOptions,
} from "./grouping-strategies/types";

export type {
  DateGroupingGranularity,
  GroupableFieldConfig,
  GroupedDataItem,
  GroupedItem,
  GroupHeader,
  GroupingOptions,
  NumberRangeConfig,
} from "./grouping-strategies/types";

export function isGroupHeader(item: GroupedItem): item is GroupHeader {
  return item.type === "group-header";
}

export function isGroupedDataItem<T extends BaseEntity>(
  item: GroupedItem<T>
): item is GroupedDataItem<T> {
  return item.type === "data";
}

export function detectGroupableFields(config: SimpleTableConfigBase<BaseEntity>): Array<{
  fieldId: string;
  label: string;
  type: string;
  groupingOptions?: GroupingOptions;
}> {
  const result: Array<{
    fieldId: string;
    label: string;
    type: string;
    groupingOptions?: GroupingOptions;
  }> = [];

  Object.entries(config.fields || {}).forEach(([fieldId, fieldConfig]) => {
    if (fieldConfig.groupable === true) {
      result.push({
        fieldId,
        label: fieldConfig.label,
        type: fieldConfig.type || "text",
        groupingOptions: (fieldConfig as GroupableFieldConfig).groupingOptions,
      });
    }
  });

  return result;
}

export function groupDataByField<T extends BaseEntity>(
  data: T[],
  fieldId: string,
  config: SimpleTableConfigBase<BaseEntity>,
  groupingOptions?: GroupingOptions
): GroupedItem<T>[] {
  const fieldConfig = config.fields?.[fieldId];
  if (!fieldConfig) return data as GroupedItem<T>[];

  const fieldType = fieldConfig.type || "text";
  const options = groupingOptions ?? (fieldConfig as GroupableFieldConfig).groupingOptions;

  switch (fieldType) {
    case "select":
    case "client-select":
      return groupByEnum(data, fieldId, fieldConfig);
    case "multi-user":
      return groupByMultiUser(data, fieldId, fieldConfig);
    case "boolean":
      return groupByBoolean(data, fieldId, fieldConfig);
    case "date":
    case "datetime":
      return groupByDate(data, fieldId, fieldConfig, options?.dateGranularity ?? "month");
    case "number":
      return groupByNumberRange(data, fieldId, fieldConfig, options?.numberRanges);
    case "tags":
      return groupByTags(data, fieldId, fieldConfig);
    default:
      return groupByText(data, fieldId, fieldConfig);
  }
}

export function groupDataByDualFields<T extends BaseEntity>(
  data: T[],
  primaryField: string,
  secondaryField: string,
  config: SimpleTableConfigBase<BaseEntity>,
  groupingOptions?: {
    primary?: GroupingOptions;
    secondary?: GroupingOptions;
  }
): GroupedItem<T>[] {
  if (!data || data.length === 0) return [];

  // A saved view can name a column that no longer exists in the config. Single
  // grouping already degrades to the ungrouped rows (see `groupDataByField`),
  // but that passthrough emits no `group-header`, so the loop below used to
  // discard EVERY row and render an empty table. Degrade the same way instead.
  if (!config.fields?.[primaryField]) {
    return groupDataByField(data, primaryField, config, groupingOptions?.primary);
  }

  const result: GroupedItem<T>[] = [];
  const primaryGrouped = groupDataByField(data, primaryField, config, groupingOptions?.primary);

  let currentPrimaryGroup: GroupHeader | null = null;
  let currentPrimaryItems: T[] = [];

  primaryGrouped.forEach((item) => {
    if (item.type === "group-header") {
      if (currentPrimaryGroup && currentPrimaryItems.length > 0) {
        processSecondaryGrouping(
          currentPrimaryGroup,
          currentPrimaryItems,
          secondaryField,
          config,
          groupingOptions?.secondary,
          result
        );
      }
      currentPrimaryGroup = {
        ...item,
        level: 1,
        isCollapsible: true,
        groupKeys: [item.groupKey],
        groupPath: [item.groupLabel],
      };
      currentPrimaryItems = [];
    } else if (item.type === "data" && currentPrimaryGroup) {
      currentPrimaryItems.push(item);
    }
  });

  if (currentPrimaryGroup && currentPrimaryItems.length > 0) {
    processSecondaryGrouping(
      currentPrimaryGroup,
      currentPrimaryItems,
      secondaryField,
      config,
      groupingOptions?.secondary,
      result
    );
  }

  return result;
}

function processSecondaryGrouping<T extends BaseEntity>(
  primaryGroup: GroupHeader,
  items: T[],
  secondaryField: string,
  config: SimpleTableConfigBase<BaseEntity>,
  secondaryOptions: GroupingOptions | undefined,
  result: GroupedItem<T>[]
): void {
  result.push({ ...primaryGroup, itemCount: items.length });

  const secondaryGrouped = groupDataByField(items, secondaryField, config, secondaryOptions);

  secondaryGrouped.forEach((item) => {
    if (item.type === "group-header") {
      result.push({
        ...item,
        level: 2,
        parentKey: primaryGroup.groupKey,
        isCollapsible: true,
        groupKeys: [primaryGroup.groupKey, item.groupKey],
        groupPath: [primaryGroup.groupLabel, item.groupLabel],
        id: `group-${primaryGroup.groupKey}-${item.groupKey}`,
      });
    } else if (item.type === "data") {
      result.push({
        ...item,
        groupKeys: [primaryGroup.groupKey, item.groupKey ?? ""],
        groupPath: [primaryGroup.groupLabel, item.groupLabel ?? item.groupKey ?? ""],
      });
    }
  });
}
